// @flow

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { head, get, filter, flow, groupBy, isEmpty, map, sortBy, uniqBy } from 'lodash/fp';
import moment from 'moment';
import { compose, withProps } from 'recompose';
import Helmet from 'react-helmet';

import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import Page from 'app/containers/parts/Page';
import Rows from 'app/components/layout/Rows';
import UserOnly from 'app/components/util/UserOnly';
import Responsive from 'app/components/responsive/Responsive';
import Section from 'app/components/commons/Section';
import Modal from 'app/components/commons/Modal';
import Button from 'app/components/commons/Button';
import Spinner from 'app/components/commons/Spinner';
import FlexiSelect from 'app/components/commons/FlexiSelect';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import { getSlates } from 'app/api/h2h';
import { getMySlate } from 'app/api/challenges';
import decodeGame from 'app/normalizers/decodeGame';
import { decodeSlate } from 'app/scenes/h2h/LoadCreateChallenge';
import getPlayersFromGames from 'app/utils/getPlayersFromGames';
import withAPICollection from 'app/hoc/withAPICollection';
import t from 'app/utils/translate';

import RacingCruncherRules from './RacingCruncherRules';
import RacingCruncherLineUps from './RacingCruncherLineUps';
import RacingCruncherSelections from './RacingCruncherSelections';
import { Row, ButtonLabel } from './components';

type SelectOption = {
  id: number,
  name: string
};

type Props = {
  darkMode?: boolean,
  isChallengeRuleTypeSelectable?: boolean,
  slates: Array<Object>,
  competitionTypes: Array<SelectOption>,
  onFetch: () => void
};

const defaultProps = {
  darkMode: false,
  isChallengeRuleTypeSelectable: false
};

const Wrapper = styled.div(({ theme }) => ({
  overflow: 'hidden',
  width: '100vw',
  [theme.mediaQuery.desktopAndUp]: {
    width: 'auto'
  }
}));

const Container = styled.div(({ numPages, pageNo }) => ({
  display: 'flex',
  transform: `translateX(${((1 - pageNo) * 100) / numPages}%)`,
  transition: '0.3s all ease-in-out',
  width: `${numPages * 100}%`
}));

const MainSection = styled.div(({ selected }) => ({
  opacity: selected ? 1 : 0,
  flexBasis: '50%',
  transition: '0.2s opacity',
  maxWidth: '50%',
  minHeight: '500px'
}));

const SelectContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap'
});

const RuleFlexiSelect = styled(FlexiSelect)(({ theme }) => ({
  flexBasis: '100%',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: '30%'
  }
}));

const TimeFlexiSelect = styled(FlexiSelect)(({ theme, fullWidth }) => ({
  flexBasis: '100%',
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: fullWidth ? '100%' : '70%'
  }
}));

const CloseButton = styled('button')(({ theme }) => ({
  color: theme.color.basis4,
  display: 'flex',
  margin: '0 auto',
  paddingTop: theme.spacing.small,
  fontSize: '0.833rem',
  textTransform: 'uppercase',
  '&:hover': {
    color: theme.color.basis2
  }
}));

const DEFAULT_CHALLENGE_RULE_TYPE = 'Leg';

const RacingCruncher: React.FC<Props> = ({
  competitionTypes,
  isChallengeRuleTypeSelectable,
  onFetch,
  slates,
  darkMode
}: Props) => {
  const [games, setGames] = useState([]);
  const [players, setPlayers] = useState(undefined);
  const [selectedCompetitionType, setSelectedCompetitionType] = useState(undefined);
  const [selectedChallengeRuleType, setSelectedChallengeRuleType] = useState(undefined);
  const [selectedStartTime, setSelectedStartTime] = useState(undefined);
  const [lineUps, setLineUps] = useState([]);
  const [cruncherRules, setCruncherRules] = useState(undefined);
  const [action, setAction] = useState(undefined);
  const [pageNo, setPageNo] = useState(1);
  const [showRules, setShowRules] = useState(false);
  const [error, setError] = useState(undefined);

  const init = () => onFetch();

  useEffect(init, []);

  const selectedSlate = (slates || [])
    .filter(
      s => !selectedCompetitionType || get('competitionType.id', s) === selectedCompetitionType.id
    )
    .filter(
      s =>
        !selectedChallengeRuleType ||
        get('challengeRuleType.id', s) === selectedChallengeRuleType.id
    )
    .filter(
      s =>
        !selectedStartTime ||
        (s.startTime === selectedStartTime.value && s.name === selectedStartTime.name)
    );

  const filteredChallengeRuleTypes = flow(
    map(({ challengeRuleType }) => challengeRuleType),
    filter('id'),
    uniqBy('id')
  )(selectedSlate);

  const filteredStartTimes = flow(
    map(({ name, startTime: value }) => ({ name, value })),
    sortBy('value')
  )(selectedSlate);

  const slateId = get('id', head(selectedSlate));
  const slateName = get('name', head(selectedSlate));

  const canLoad = () =>
    action !== 'loading' &&
    selectedCompetitionType &&
    selectedChallengeRuleType &&
    selectedStartTime &&
    selectedSlate.length > 0;

  const handleLoadSlate = () => {
    if (!canLoad()) {
      return;
    }

    setAction('loading');
    setGames([]);
    setPlayers(undefined);

    getMySlate(slateId, { embed: 'games.players,cruncher_rule' })
      .then(({ data }) => {
        setCruncherRules({
          minPlayersPerGame: get('cruncher_rule.players_in_game.min', data),
          maxPlayersPerGame: get('cruncher_rule.players_in_game.max', data),
          minPlayersPerSlate: get('cruncher_rule.players_in_slate.min', data),
          maxPlayersPerSlate: get('cruncher_rule.players_in_slate.max', data)
        });
        setGames(sortBy('start_time', Object.values(data.games)).map(decodeGame));
        setPlayers(groupBy('gameId', getPlayersFromGames(data.games)));
      })
      .catch(({ errors }) => setError(get('message', errors)))
      .finally(() => setAction(undefined));
  };

  const handleBack = () => {
    setLineUps([]);
    setPageNo(1);
  };

  const handleDeleteLineUps = () => {
    setLineUps([]);
    setPageNo(1);
  };

  const handleStartOver = () => {
    handleDeleteLineUps();
  };

  const handleCrunchSuccess = data => {
    setLineUps(data);
    setPageNo(2);
  };

  return (
    <UserOnly>
      <Page
        title={t('Combo Calculator')}
        onBack={pageNo > 1 ? handleBack : undefined}
        disableReferrerBack
      >
        <Responsive desktop>
          {isDesktop => (
            <Wrapper>
              <Helmet>{t('Racing Cruncher')}</Helmet>
              <Container pageNo={pageNo} numPages={2}>
                <MainSection selected={pageNo === 1}>
                  <Rows desktop={2} mobile={1}>
                    <Section
                      title="Select Competition"
                      renderRightElement={() =>
                        isDesktop ? (
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            size="lg"
                            onClick={() => setShowRules(true)}
                            style={{ marginLeft: '1rem', cursor: 'pointer' }}
                          />
                        ) : null
                      }
                    >
                      <FlexiSelect
                        loading={false}
                        onChange={value => {
                          setSelectedCompetitionType(
                            (competitionTypes || []).find(ct => ct.id === value)
                          );
                          setSelectedChallengeRuleType(
                            isChallengeRuleTypeSelectable
                              ? undefined
                              : filteredChallengeRuleTypes.find(
                                  ({ name }) => name === DEFAULT_CHALLENGE_RULE_TYPE
                                )
                          );
                          setSelectedStartTime(undefined);
                        }}
                        options={competitionTypes.map(({ id, name, sportIcon }) => ({
                          value: id,
                          label: name,
                          icon: `icon-sport-${sportIcon}`
                        }))}
                        placeholder="Select Competition"
                        value={get('id', selectedCompetitionType)}
                      />
                    </Section>
                    {!!selectedCompetitionType && (
                      <Section title="Select Slate">
                        <SelectContainer>
                          {isChallengeRuleTypeSelectable && (
                            <RuleFlexiSelect
                              loading={false}
                              onChange={value =>
                                setSelectedChallengeRuleType(
                                  filteredChallengeRuleTypes.find(rt => rt.id === value)
                                )
                              }
                              options={filteredChallengeRuleTypes.map(({ id, name }) => ({
                                value: id,
                                label: name
                              }))}
                              placeholder="Select Rule"
                              value={get('id', selectedChallengeRuleType)}
                            />
                          )}
                          {!!selectedChallengeRuleType && (
                            <TimeFlexiSelect
                              loading={false}
                              onChange={value =>
                                setSelectedStartTime(
                                  (filteredStartTimes || []).find(
                                    st => `${st.value}-${st.name}` === value
                                  )
                                )
                              }
                              options={(filteredStartTimes || []).map(st => ({
                                value: `${st.value}-${st.name}`,
                                label: `${moment(st.value).format('DD/MM/YYYY - h:mm a')} ${
                                  st.name
                                }`
                              }))}
                              placeholder="Select Start Time"
                              value={
                                selectedStartTime
                                  ? `${get('value', selectedStartTime)}-${get(
                                      'name',
                                      selectedStartTime
                                    )}`
                                  : undefined
                              }
                              fullWidth={!isChallengeRuleTypeSelectable}
                            />
                          )}
                        </SelectContainer>
                      </Section>
                    )}
                  </Rows>
                  <Rows desktop={2} mobile={1}>
                    <Row>
                      <Button
                        block
                        autoBorderRadius={false}
                        kind="success"
                        disabled={!canLoad()}
                        onClick={handleLoadSlate}
                      >
                        <div className="buttonInner">
                          {action === 'loading' && <Spinner size="small" inlineLeft />}
                          <ButtonLabel>
                            {t(`${action === 'loading' ? 'Loading...' : 'Load Slate'}`)}
                          </ButtonLabel>
                        </div>
                      </Button>
                    </Row>
                  </Rows>
                  {error && <FormValidationMessage message={error} />}
                  {games.length > 0 && !isEmpty(players) && (
                    <RacingCruncherSelections
                      cruncherRules={cruncherRules}
                      darkMode={darkMode}
                      isDesktop={isDesktop}
                      games={games}
                      players={players}
                      slateId={slateId}
                      onCrunchSuccess={handleCrunchSuccess}
                      onShowRules={() => setShowRules(true)}
                    />
                  )}
                </MainSection>
                <MainSection selected={pageNo === 2}>
                  {lineUps.length > 0 && (
                    <RacingCruncherLineUps
                      lineUpsData={lineUps}
                      isDesktop={isDesktop}
                      slateId={slateId}
                      slateName={slateName}
                      onDeleteLineUps={handleDeleteLineUps}
                      onStartOver={handleStartOver}
                    />
                  )}
                </MainSection>
              </Container>
            </Wrapper>
          )}
        </Responsive>
        {showRules && (
          <Modal isOpen size="medium" onCancel={() => setShowRules(false)}>
            <RacingCruncherRules />
            <CloseButton type="button" onClick={() => setShowRules(false)}>
              Close
            </CloseButton>
          </Modal>
        )}
        <NotificationHandler
          afterCond={!action}
          beforeCond={action === 'downloading'}
          type="success"
        >
          <span>
            Downloading, choose &quot;save&quot; if prompted
            <br />
          </span>
        </NotificationHandler>
      </Page>
    </UserOnly>
  );
};

const apiWrapper = compose(
  withAPICollection({
    fetcher: () => getSlates({ is_leg: true }),
    normalizeData: map(decodeSlate)
  }),
  withProps(({ items }) => ({
    slates: items,
    competitionTypes: items
      ? flow(uniqBy('id'), sortBy('name'))(items.map(({ competitionType }) => competitionType))
      : []
  }))
);

const connector = connect(({ settings }) => ({
  darkMode: settings.darkMode
}));

RacingCruncher.defaultProps = defaultProps;

export default compose(apiWrapper, connector)(RacingCruncher);
